import React, { useState, useEffect } from "react";
import CoachPractice from "./CoachPractice";
import { Card, Button, Form, ProgressBar } from "react-bootstrap";

const getNamespacedKey = (key) => {
  const userId = localStorage.getItem("user_id");
  return userId ? `${key}_${userId}` : key;
};
const STORAGE_KEY = getNamespacedKey("coachState");

const SUGGESTED_SCENARIOS = [
  "Navigating conflict with a peer",
  "Giving difficult feedback to a team member",
  "Negotiating with a tough client",
  "Handling performance anxiety before a presentation",
  "Dealing with rejection after a pitch",
  "Setting boundaries with senior leadership"
];

function Typewriter({ options, onSelect, value }) {
  const [phIdx, setPhIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [displayed, setDisplayed] = useState(options[0]);
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (clicked) {
      setDisplayed(options[phIdx]);
      const pause = setTimeout(() => setClicked(false), 1800);
      return () => clearTimeout(pause);
    }
    if (value && value === options[phIdx]) {
      setDisplayed(value);
      setCharIdx(value.length);
      return;
    }
    const current = options[phIdx];
    if (charIdx < current.length) {
      const t = setTimeout(() => {
        setDisplayed(current.slice(0, charIdx + 1));
        setCharIdx(c => c + 1);
      }, 40);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setCharIdx(0);
      setPhIdx(i => (i + 1) % options.length);
    }, 1100);
    return () => clearTimeout(t);
  }, [phIdx, charIdx, options, clicked, value]);

  return (
    <div style={{ color: "#569e56", fontSize: 17, margin: "10px 0 0 0", cursor: "pointer" }}>
      <span
        onClick={() => {
          setClicked(true);
          onSelect(options[phIdx]);
        }}
        style={{ userSelect: "none" }}
      >
        {displayed}
        <span style={{ opacity: 0.4 }}>|</span>
      </span>
      <span style={{ fontSize: 13, color: "#888", marginLeft: 13 }}>(click to use)</span>
    </div>
  );
}

const INITIAL_INPUTS = {
  scenario: "",
  reaction: "",
  setting: "",
  reflection: "",
  accountability: "",
  antiReassurance: "",
  scenarios: [],
  scenarioResponses: [],
  scenarioFeedbacks: [],
  completed: false,
  closure: "",
};

export default function Coach() {
  // -- Restore from LS or blank
  const saved = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  })();

  const [step, setStep] = useState(saved.step ?? 0);
  const [inputs, setInputs] = useState(saved.inputs ?? { ...INITIAL_INPUTS });
  const [progress, setProgress] = useState(saved.progress ?? 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // -- Persist to LS on every change
  useEffect(() => {
    const state = { step, inputs, progress };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [step, inputs, progress]);

  const handleInput = (field, value) =>
    setInputs(prev => ({ ...prev, [field]: value }));

  const nextStep = () => setStep(s => s + 1);

  // -- FULL RESET, like MindMap etc.
  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setStep(0);
    setInputs({ ...INITIAL_INPUTS });
    setProgress(0);
    setLoading(false);
    setError("");
  };

  if (step === 0) {
    return (
      <Card className="p-4" style={{ maxWidth: 900, margin: "32px auto 0", boxShadow: "0 4px 32px #e9efe9" }}>
        <h3 style={{ fontWeight: 600, marginBottom: 10 }}>Coach: Scenario Intake</h3>
        <Form.Group className="mb-3">
          <Form.Label>What scenario or challenge do you want to work on?</Form.Label>
          <Form.Control
            autoFocus
            as="textarea"
            rows={4}
            style={{ minHeight: 90, fontSize: 17 }}
            value={inputs.scenario}
            onChange={e => handleInput("scenario", e.target.value)}
            placeholder="Describe a real, high-stakes situation..."
          />
          <Typewriter options={SUGGESTED_SCENARIOS} onSelect={val => handleInput("scenario", val)} value={inputs.scenario} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>How do you typically respond in this situation?</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            style={{ minHeight: 70, fontSize: 17 }}
            value={inputs.reaction}
            onChange={e => handleInput("reaction", e.target.value)}
            placeholder="Your default response, even if it's not ideal..."
          />
        </Form.Group>
        <Form.Group className="mb-4">
          <Form.Label>Where or with whom does this scenario usually take place?</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            style={{ minHeight: 70, fontSize: 17 }}
            value={inputs.setting}
            onChange={e => handleInput("setting", e.target.value)}
            placeholder="(e.g. 'At work with my manager', 'On calls with clients', etc)"
          />
        </Form.Group>
        <div className="d-flex justify-content-between align-items-center">
          <Button
            variant="primary"
            style={{ minWidth: 120, fontWeight: 500, fontSize: 18 }}
            disabled={!inputs.scenario.trim() || !inputs.reaction.trim() || !inputs.setting.trim()}
            onClick={nextStep}
          >
            Start
          </Button>
          <ProgressBar style={{ width: 210 }} now={12} variant="info" label="Step 1 of 5" />
        </div>
      </Card>
    );
  }

  return (
    <CoachPractice
      inputs={inputs}
      setInputs={setInputs}
      step={step}
      setStep={setStep}
      progress={progress}
      setProgress={setProgress}
      STORAGE_KEY={STORAGE_KEY}
      loading={loading}
      setLoading={setLoading}
      error={error}
      setError={setError}
      onReset={handleReset}
    />
  );
}
